import {
  AlertCircle,
  Loader2,
  Maximize2,
  Mic,
  Send,
  Square,
  Video,
  Volume2,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config';
import { CampaignVoiceInput } from './CampaignVoiceInput';

const DEFAULT_AVATAR_ID = process.env.REACT_APP_LIVEAVATAR_DEFAULT_AVATAR_ID || '65f9e3c9-d48b-4118-b73a-4ae2e3cbb8f0';
const DEFAULT_CONTEXT_ID = process.env.REACT_APP_LIVEAVATAR_CONTEXT_ID || '158f5d55-2d4f-11f1-8d28-066a7fa2e369';
const USE_SANDBOX_EMBED = process.env.REACT_APP_LIVEAVATAR_USE_SANDBOX_EMBED === 'true';

const formatTime = () =>
  new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const createEventId = () =>
  `alice-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const streamBase64PcmToLiveAvatar = async (session, base64Audio) => {
  const socket = session?._sessionEventSocket;
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    throw new Error('LiveAvatar websocket is not open.');
  }

  const binary = atob(base64Audio);
  const eventId = createEventId();
  const bytesPerChunk = 480 * 2;

  for (let index = 0; index < binary.length; index += bytesPerChunk) {
    const audioChunk = binary.slice(index, index + bytesPerChunk);
    socket.send(JSON.stringify({
      type: 'agent.speak',
      event_id: eventId,
      audio: btoa(audioChunk),
    }));

    // The realtime API expects audio to be streamed in PCM chunks.
    await sleep(20);
  }

  socket.send(JSON.stringify({
    type: 'agent.speak_end',
    event_id: eventId,
  }));
};

export function LiveAvatarVideoPanel({
  sessionId,
  messages = [],
  onSubmit,
  disabled = false,
  previewMode = false,
}) {
  const avatarSessionRef = useRef(null);
  const videoRef = useRef(null);
  const lastSpokenRef = useRef('');
  const lastAvatarTranscriptRef = useRef('');
  const isSubmittingTranscriptRef = useRef(false);
  const listeningResumeTimerRef = useRef(null);
  const onSubmitRef = useRef(onSubmit);
  const disabledRef = useRef(disabled);
  const [status, setStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('Video not started');
  const [error, setError] = useState('');
  const [textInput, setTextInput] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [diagnosticMode, setDiagnosticMode] = useState(USE_SANDBOX_EMBED);
  const [bridgeEvents, setBridgeEvents] = useState([]);

  const addBridgeEvent = useCallback((message, tone = 'info') => {
    const event = {
      message,
      tone,
      time: formatTime(),
    };

    setBridgeEvents((current) => [event, ...current].slice(0, 8));
    if (tone === 'error') {
      console.warn(`[LiveAvatar bridge] ${message}`);
    } else {
      console.info(`[LiveAvatar bridge] ${message}`);
    }
  }, []);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  const stopSession = useCallback(async () => {
    const session = avatarSessionRef.current;
    avatarSessionRef.current = null;
    setEmbedUrl('');
    lastSpokenRef.current = '';
    window.clearTimeout(listeningResumeTimerRef.current);
    setIsSpeaking(false);

    if (!session) {
      setStatus('idle');
      setStatusMessage('Video not started');
      return;
    }

    setStatus('ending');
    setStatusMessage('Stopping video session...');

    try {
      if (typeof session.stop === 'function') {
        await session.stop();
      } else if (typeof session.disconnect === 'function') {
        await session.disconnect();
      }
    } catch (err) {
      console.warn('LiveAvatar cleanup failed:', err);
    } finally {
      setStatus('idle');
      setStatusMessage('Video stopped');
    }
  }, []);

  useEffect(() => {
    return () => {
      stopSession();
    };
  }, [stopSession]);

  const sendTextSpeakCommand = useCallback((session, text) => {
    if (typeof session.message === 'function') {
      session.message(text);
    } else if (typeof session.repeat === 'function') {
      session.repeat(text);
    }
  }, []);

  const speakWithAvatar = useCallback(async (text) => {
    const trimmed = String(text || '').trim();
    const session = avatarSessionRef.current;
    if (!trimmed) {
      addBridgeEvent('No ALICE text available to speak.', 'warn');
      return;
    }

    if (!session) {
      addBridgeEvent('ALICE text exists, but no LiveAvatar session is active.', 'warn');
      return;
    }

    if (lastSpokenRef.current === trimmed) {
      addBridgeEvent('Skipped duplicate ALICE message already sent to avatar.', 'info');
      return;
    }

    try {
      lastSpokenRef.current = trimmed;
      addBridgeEvent(`Sending ALICE response to avatar (${trimmed.length} chars).`);

      if (typeof session.repeatAudio === 'function' && sessionId) {
        const hasEventSocket = Boolean(session._sessionEventSocket);
        const roomState = session.room?.state || 'unknown';
        addBridgeEvent(`LiveAvatar transport: websocket=${hasEventSocket ? 'yes' : 'no'}, room=${roomState}.`);

        if (!hasEventSocket) {
          setError('LiveAvatar connected, but no websocket control channel is available for repeatAudio.');
          addBridgeEvent('Cannot use repeatAudio because LiveAvatar did not expose a websocket control channel.', 'error');
          sendTextSpeakCommand(session, trimmed);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${sessionId}/video/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: trimmed }),
        });

        const data = await response.json();
        if (response.ok && data.audio) {
          addBridgeEvent(`TTS audio ready (${data.audio.length} base64 chars). Streaming PCM chunks.`);
          session.stopListening?.();
          session.interrupt?.();
          addBridgeEvent('Paused avatar listening before sending ALICE audio.');
          await streamBase64PcmToLiveAvatar(session, data.audio);
          window.setTimeout(() => {
            if (!isSpeaking && lastSpokenRef.current === trimmed) {
              addBridgeEvent('Audio chunks were streamed, but LiveAvatar has not emitted speak_started yet.', 'warn');
            }
          }, 5000);
          return;
        }

        console.warn('LiveAvatar TTS audio unavailable:', data.error || response.statusText);
        addBridgeEvent(`TTS failed: ${data.error || response.statusText}`, 'error');
      }

      addBridgeEvent('Falling back to LiveAvatar text speak command.');
      sendTextSpeakCommand(session, trimmed);
    } catch (err) {
      console.warn('Could not send text to LiveAvatar:', err);
      addBridgeEvent(`Avatar speak failed: ${err.message || err}`, 'error');
      setError('ALICE responded, but the avatar could not speak the response.');
    }
  }, [addBridgeEvent, isSpeaking, sendTextSpeakCommand, sessionId]);

  const submitToCampaignBrain = useCallback(async (message) => {
    const trimmed = String(message || '').trim();
    if (!trimmed || disabledRef.current || isSubmittingTranscriptRef.current) return null;

    isSubmittingTranscriptRef.current = true;
    setStatus(avatarSessionRef.current ? 'thinking' : status);
    setStatusMessage(avatarSessionRef.current ? 'ALICE is thinking...' : statusMessage);

    try {
      const result = await onSubmitRef.current?.(trimmed);
      if (result?.aliceText) {
        addBridgeEvent('Campaign brain returned ALICE text.');
        await speakWithAvatar(result.aliceText);
      } else {
        addBridgeEvent('Campaign brain returned no aliceText to speak.', 'warn');
      }
      return result;
    } finally {
      isSubmittingTranscriptRef.current = false;
      if (avatarSessionRef.current) {
        setStatus('connected');
        setStatusMessage('Agent connected');
      }
    }
  }, [addBridgeEvent, speakWithAvatar, status, statusMessage]);

  useEffect(() => {
    const latestAliceMessage = [...messages]
      .reverse()
      .find((message) => message.sender === 'alice' || message.sender === 'system');

    if (status === 'connected' || status === 'speaking') {
      speakWithAvatar(latestAliceMessage?.text);
    }
  }, [messages, speakWithAvatar, status]);

  const startSession = async ({ useEmbed = diagnosticMode } = {}) => {
    if (previewMode) {
      setError('Video mode requires a real campaign session.');
      return;
    }

    if (!sessionId || avatarSessionRef.current || embedUrl) return;

    setError('');
    setStatus('starting');
    setStatusMessage('Starting ALICE video...');

    try {
      const sandboxMode = process.env.NODE_ENV !== 'production';
      if (sandboxMode && useEmbed) {
        const response = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${sessionId}/video/embed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            avatar_id: DEFAULT_AVATAR_ID,
            context_id: DEFAULT_CONTEXT_ID,
            sandbox: true,
          }),
        });

        const data = await response.json();
        if (!response.ok || !data.url) {
          throw new Error(data.error || 'Failed to create LiveAvatar embed.');
        }

        setEmbedUrl(data.url);
        setStatus('connected');
        setStatusMessage('Connected in sandbox embed mode');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${sessionId}/video/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          avatar_id: DEFAULT_AVATAR_ID,
          sandbox: sandboxMode,
          mode: process.env.REACT_APP_LIVEAVATAR_SESSION_MODE || 'LITE',
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.token) {
        throw new Error(data.error || 'Failed to start LiveAvatar session.');
      }

      const {
        AgentEventsEnum,
        LiveAvatarSession,
        SessionEvent,
      } = await import('@heygen/liveavatar-web-sdk');

      const liveSession = new LiveAvatarSession(data.token, {
        voiceChat: { defaultMuted: false },
      });
      avatarSessionRef.current = liveSession;
      addBridgeEvent(`LiveAvatar token accepted. mode=${data.mode || 'unknown'}, sandbox=${data.sandbox ? 'yes' : 'no'}.`);

      liveSession.on(SessionEvent.SESSION_STREAM_READY, () => {
        if (videoRef.current && typeof liveSession.attach === 'function') {
          liveSession.attach(videoRef.current);
          videoRef.current.play?.().catch(() => {});
        }
        addBridgeEvent('LiveAvatar stream is ready and attached.');
        setStatus('connected');
        setStatusMessage(data.sandbox ? 'Agent connected in sandbox mode' : 'Agent connected');
      });

      liveSession.on(SessionEvent.SESSION_STATE_CHANGED, (nextState) => {
        addBridgeEvent(`LiveAvatar state changed: ${nextState}.`);
        if (nextState === 'CONNECTED') {
          setStatus('connected');
          setStatusMessage(data.sandbox ? 'Agent connected in sandbox mode' : 'Agent connected');
        }
      });

      liveSession.on(SessionEvent.SESSION_DISCONNECTED, () => {
        addBridgeEvent('LiveAvatar session disconnected.', 'warn');
        avatarSessionRef.current = null;
        setIsSpeaking(false);
        setStatus('idle');
        setStatusMessage('Video session ended');
      });

      liveSession.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
        window.clearTimeout(listeningResumeTimerRef.current);
        addBridgeEvent('LiveAvatar emitted speak_started.');
        setIsSpeaking(true);
        setStatus('speaking');
        setStatusMessage('ALICE is speaking');
      });

      liveSession.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
        addBridgeEvent('LiveAvatar emitted speak_ended.');
        setIsSpeaking(false);
        setStatus('connected');
        setStatusMessage(data.sandbox ? 'Agent connected in sandbox mode' : 'Agent connected');
        listeningResumeTimerRef.current = window.setTimeout(() => {
          liveSession.startListening?.();
          addBridgeEvent('Resumed avatar listening after speech.');
        }, 300);
      });

      liveSession.on(AgentEventsEnum.USER_TRANSCRIPTION, (event) => {
        const transcript = String(event?.text || '').trim();
        if (!transcript || transcript === lastAvatarTranscriptRef.current) return;

        addBridgeEvent(`LiveAvatar transcript received: "${transcript.slice(0, 80)}"`);
        lastAvatarTranscriptRef.current = transcript;
        submitToCampaignBrain(transcript);
      });

      await liveSession.start();
      addBridgeEvent(`LiveAvatar started. websocket=${liveSession._sessionEventSocket ? 'yes' : 'no'}, room=${liveSession.room?.state || 'unknown'}.`);
      liveSession.stopListening?.();
      setStatus('connected');
      setStatusMessage(data.sandbox ? 'Agent connected in sandbox mode' : 'Agent connected');

      const latestAliceMessage = [...messages]
        .reverse()
        .find((message) => message.sender === 'alice' || message.sender === 'system');
      if (latestAliceMessage?.text) {
        addBridgeEvent('Sending latest visible ALICE message after avatar start.');
        speakWithAvatar(latestAliceMessage.text);
      }
    } catch (err) {
      console.error('LiveAvatar start failed:', err);
      addBridgeEvent(`LiveAvatar start failed: ${err.message || err}`, 'error');
      avatarSessionRef.current = null;
      setStatus('error');
      setStatusMessage('Video unavailable');
      setError(err.message || 'Failed to start video mode.');
    }
  };

  const submitText = async () => {
    const message = textInput.trim();
    if (!message || disabled) return;

    setTextInput('');
    await submitToCampaignBrain(message);
  };

  const submitVoice = async (message) => {
    await submitToCampaignBrain(message);
  };

  const connectionClass =
    status === 'connected' || status === 'speaking'
      ? 'bg-green-600'
      : status === 'starting' || status === 'thinking' || status === 'ending'
        ? 'bg-amber-500'
        : status === 'error'
          ? 'bg-red-600'
          : 'bg-gray-700';

  return (
    <div className="flex-1 overflow-y-auto bg-white px-6 py-6">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.65fr)_minmax(320px,0.9fr)]">
        <section className="min-w-0">
          <div className="relative aspect-video overflow-hidden rounded-2xl bg-black shadow-sm">
            {embedUrl ? (
              <iframe
                src={embedUrl}
                allow="microphone"
                title="LiveAvatar Embed"
                className="h-full w-full border-0 bg-black"
              />
            ) : (
              <video
                ref={videoRef}
                className="h-full w-full bg-black object-cover"
                autoPlay
                playsInline
              />
            )}

            {!embedUrl && status !== 'connected' && status !== 'speaking' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950 text-white">
                {status === 'starting' ? (
                  <Loader2 className="mb-3 h-10 w-10 animate-spin text-purple-300" />
                ) : status === 'error' ? (
                  <AlertCircle className="mb-3 h-10 w-10 text-red-300" />
                ) : (
                  <Video className="mb-3 h-10 w-10 text-purple-300" />
                )}
                <div className="text-sm font-medium">{statusMessage}</div>
                <div className="mt-1 max-w-sm text-center text-xs text-gray-300">
                  Start video to speak with ALICE through LiveAvatar.
                </div>
              </div>
            )}

            <div className={`absolute right-4 top-4 rounded-full px-3 py-1 text-xs font-semibold text-white ${connectionClass}`}>
              {statusMessage}
            </div>

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-white/70">Mode</div>
                  <div className="text-sm font-semibold">Video</div>
                </div>
                <div>
                  <div className="text-white/70">Session</div>
                  <div className="text-sm font-semibold">{sessionId ? 'Campaign' : 'Preview'}</div>
                </div>
                <div>
                  <div className="text-white/70">Speech</div>
                  <div className="text-sm font-semibold">{isSpeaking ? 'Speaking' : 'Ready'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-gray-50 p-3">
            {avatarSessionRef.current || embedUrl ? (
              <button
                type="button"
                onClick={stopSession}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700"
              >
                <Square className="h-4 w-4" />
                Stop Video
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setDiagnosticMode(false);
                    startSession({ useEmbed: false });
                  }}
                  disabled={disabled || status === 'starting'}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {status === 'starting' && !diagnosticMode ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
                  Start Agent Video
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setDiagnosticMode(true);
                    startSession({ useEmbed: true });
                  }}
                  disabled={disabled || status === 'starting'}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {status === 'starting' && diagnosticMode ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />}
                  Sandbox Test
                </button>
              </>
            )}

            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
              disabled
            >
              <Volume2 className="h-4 w-4" />
              Live mic enabled
            </button>

            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
              disabled
            >
              <Maximize2 className="h-4 w-4" />
              Fullscreen
            </button>
          </div>

          {error && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {bridgeEvents.length > 0 && (
            <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Avatar Bridge Diagnostics
              </div>
              <div className="space-y-2">
                {bridgeEvents.map((event, index) => (
                  <div
                    key={`${event.time}-${index}-${event.message}`}
                    className={`rounded-lg px-3 py-2 text-xs ${
                      event.tone === 'error'
                        ? 'bg-red-50 text-red-700'
                        : event.tone === 'warn'
                          ? 'bg-amber-50 text-amber-800'
                          : 'bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className="mr-2 font-medium text-gray-500">{event.time}</span>
                    {event.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <aside className="min-h-[420px] rounded-2xl border border-gray-200 bg-gray-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Conversation Transcript</h2>
            <span className="text-xs text-gray-500">{messages.length} messages</span>
          </div>

          <div className="max-h-[510px] space-y-3 overflow-y-auto pr-1">
            {messages.map((message, index) => {
              const isAlice = message.sender === 'alice' || message.sender === 'system';
              return (
                <div
                  key={`${message.sender}-${index}`}
                  className={`rounded-lg border-l-4 bg-white p-3 shadow-xs ${
                    isAlice ? 'border-orange-500' : 'border-blue-500'
                  }`}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">
                      {isAlice ? 'ALICE' : 'You'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {message.timestamp || formatTime()}
                    </span>
                  </div>
                  <p className="text-sm leading-6 text-gray-700">{message.text}</p>
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      <div className="mx-auto mt-5 max-w-6xl rounded-2xl border border-gray-200 bg-white">
        <CampaignVoiceInput
          onSubmit={submitVoice}
          disabled={disabled}
          placeholder="Speak to ALICE about this campaign..."
          autoSubmit
          autoSubmitDelayMs={1400}
        />

        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-3 focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-purple-200">
            <Mic className="h-5 w-5 text-gray-400" />
            <input
              value={textInput}
              onChange={(event) => setTextInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submitText();
                }
              }}
              disabled={disabled}
              placeholder="Type instead..."
              className="flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
            />
            <button
              type="button"
              onClick={submitText}
              disabled={disabled || !textInput.trim()}
              className="inline-flex items-center gap-2 rounded-lg bg-gray-950 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LiveAvatarVideoPanel;
